// Store root — Isomorphism Phase 2 · AppState slicing
//
// Combines all slices into a single Zustand store. Each slice owns its own
// state domain; cross-slice reads happen via selectors.
//
// Usage in components:
//   import { useStore } from './store';
//   const draft = useStore((s) => s.draft);
//   const theme = useStore((s) => s.theme);
//
// Isomorphism mapping:
//   - Blind Spot 1 (field-level context isolation): slices enforce write boundaries
//   - tech-decisions.md §1 Layer 1 Kernel "State (单向数据流，事件总线)"
//   - Claude Code's createSubagentContext: per-field access control

import { create } from 'zustand';
import type { CourseSlice } from './course-slice.js';
import { createCourseSlice } from './course-slice.js';
import type { IngestSlice } from './ingest-slice.js';
import { createIngestSlice } from './ingest-slice.js';
import type { UiSlice } from './ui-slice.js';
import { createUiSlice } from './ui-slice.js';
import type { RegenSlice } from './regen-slice.js';
import { createRegenSlice } from './regen-slice.js';
import type { NotificationSlice } from './notification-slice.js';
import { createNotificationSlice } from './notification-slice.js';
import type { AuthSlice } from './auth-slice.js';
import { createAuthSlice } from './auth-slice.js';
import type { AccountSlice } from './account-slice.js';
import { createAccountSlice } from './account-slice.js';
import type { DiagnoseSlice } from './diagnose-slice.js';
import { createDiagnoseSlice } from './diagnose-slice.js';
import type { SaveSlice } from './save-slice.js';
import { createSaveSlice } from './save-slice.js';

/** Combined store type — all slices merged. */
export type AppStore = CourseSlice & IngestSlice & UiSlice & RegenSlice & NotificationSlice & AuthSlice & AccountSlice & DiagnoseSlice & SaveSlice;

/** Combined slice creators in order. */
const sliceCreators = [
  createCourseSlice,
  createIngestSlice,
  createUiSlice,
  createRegenSlice,
  createNotificationSlice,
  createAuthSlice,
  createAccountSlice,
  createDiagnoseSlice,
  createSaveSlice,
] as const;

/**
 * Create the combined Zustand store.
 *
 * Each slice creator is a StateCreator that receives the full store's
 * set/get but only writes to its own fields. The composition pattern
 * from Zustand docs: pass each creator through the chain.
 */
function composeSlices<S extends AppStore>(
  ...creators: Array<StateCreator<S>>
): StateCreator<S> {
  return (set, get, store) => {
    let result = {} as S;
    for (const creator of creators) {
      const slice = creator(set, get, store);
      result = { ...result, ...slice };
    }
    return result;
  };
}

import type { StateCreator } from 'zustand';

export const useStore = create<AppStore>()(
  composeSlices<AppStore>(
    ...sliceCreators as unknown as Array<StateCreator<AppStore>>,
  ),
);
